/* ChunkyFit Bundle Builder — 4-Box Offer
   Manages tier selection, flavor quantities, free-box math,
   nutrition modal, gallery sync, mobile sticky reveal,
   and cart submission. */

class CfBundleBuilder extends HTMLElement {
  constructor() {
    super();
    this.state = new Map();
    this.packPrice = parseFloat(this.dataset.packPrice) || 15.99;
    this.tier1 = { qty: parseInt(this.dataset.tier1Qty, 10) || 5,  free: parseInt(this.dataset.tier1Free, 10) || 1 };
    this.tier2 = { qty: parseInt(this.dataset.tier2Qty, 10) || 10, free: parseInt(this.dataset.tier2Free, 10) || 2 };
    this.tier = this.tier1.qty;
    this.freeQty = this.tier1.free;
    this.currency = this.dataset.currency || '$';
    this.discountCode = (this.dataset.discountCode || '').trim();
  }

  connectedCallback() {
    this.cacheNodes();
    this.bindEvents();
    this.render();
  }

  cacheNodes() {
    this.$ = {
      fill: this.querySelector('[data-progress-fill]'),
      cur: this.querySelector('[data-progress-current]'),
      tgt: this.querySelector('[data-progress-target]'),
      msg: this.querySelector('[data-progress-message]'),
      btn: this.querySelector('.cf-btn-cta[data-checkout-button]'),
      label: this.querySelector('[data-cta-label]'),
      prices: this.querySelector('[data-cta-prices]'),
      strike: this.querySelector('[data-cta-strike]'),
      total: this.querySelector('[data-cta-total]')
    };
    this.$m = {
      fill: document.querySelector('[data-m-fill]'),
      cur: document.querySelector('[data-m-current]'),
      tgt: document.querySelector('[data-m-target]'),
      msg: document.querySelector('[data-m-message]'),
      btn: document.querySelector('[data-m-button]'),
      label: document.querySelector('[data-m-label]'),
      prices: document.querySelector('[data-m-prices]'),
      strike: document.querySelector('[data-m-strike]'),
      total: document.querySelector('[data-m-total]')
    };
    this.tiers = Array.from(this.querySelectorAll('.cf-tier'));
    this.cards = Array.from(this.querySelectorAll('[data-flavor-card]'));
    this.cards.forEach((c) => {
      this.state.set(c.dataset.flavorId, {
        name: c.dataset.flavorName,
        price: parseFloat(c.dataset.flavorPrice) || this.packPrice,
        qty: 0,
        variantId: c.dataset.variantId || null,
        nutritionImage: c.dataset.nutritionImage || null,
        galleryImage: c.dataset.galleryImage || null
      });
    });
  }

  bindEvents() {
    this.tiers.forEach((t) => {
      t.addEventListener('click', () => {
        this.selectTier(parseInt(t.dataset.tier, 10), parseInt(t.dataset.free, 10));
      });
    });
    this.cards.forEach((c) => {
      const id = c.dataset.flavorId;
      c.querySelector('[data-increment]')?.addEventListener('click', () => this.delta(id, 1));
      c.querySelector('[data-decrement]')?.addEventListener('click', () => this.delta(id, -1));
      // Nutrition modal trigger
      c.querySelector('[data-nutrition-trigger]')?.addEventListener('click', (e) => {
        e.preventDefault();
        const card = e.currentTarget.closest('[data-flavor-card]');
        openCfNutrition({
          name: card.dataset.flavorName,
          image: card.dataset.nutritionImage,
          galleryImage: card.dataset.galleryImage
        });
      });
    });
    this.$.btn?.addEventListener('click', () => this.checkout());
    this.$m.btn?.addEventListener('click', () => this.checkout());
  }

  selectTier(qty, free) {
    this.tier = qty;
    this.freeQty = free;
    this.tiers.forEach((t) => t.classList.toggle('is-selected', parseInt(t.dataset.tier, 10) === qty));
    // Auto-trim if currently selected exceeds new tier
    let total = 0; this.state.forEach((i) => total += i.qty);
    while (total > qty) {
      let max = null, maxQ = 0;
      this.state.forEach((v, k) => { if (v.qty > maxQ) { maxQ = v.qty; max = k; } });
      if (!max) break;
      this.state.get(max).qty--;
      total--;
    }
    this.render();
  }

  delta(id, d) {
    const it = this.state.get(id);
    if (!it) return;
    const { qty } = this.totals();
    if (d > 0 && qty >= this.tier) return;
    it.qty = Math.max(0, it.qty + d);
    this.render();
  }

  totals() {
    let qty = 0, subtotal = 0;
    this.state.forEach((i) => { qty += i.qty; subtotal += i.qty * i.price; });
    const freeUnits = qty >= this.tier ? this.freeQty : 0;
    let discount = 0;
    if (freeUnits > 0) {
      const prices = [];
      this.state.forEach((i) => { for (let j = 0; j < i.qty; j++) prices.push(i.price); });
      prices.sort((a, b) => a - b);
      for (let i = 0; i < Math.min(freeUnits, prices.length); i++) discount += prices[i];
    }
    return { qty, subtotal, discount, total: Math.max(0, subtotal - discount) };
  }

  fmt(n) {
    return this.currency + (Math.round(n * 100) / 100).toFixed(2);
  }

  render() {
    const { qty, subtotal, total } = this.totals();
    const remaining = Math.max(0, this.tier - qty);
    const pct = Math.min(100, (qty / this.tier) * 100);

    // Progress bar removed — count text only
    [this.$.cur, this.$m.cur].forEach((el) => { if (el) el.textContent = qty; });
    [this.$.tgt, this.$m.tgt].forEach((el) => { if (el) el.textContent = this.tier; });

    let msg;
    if (qty === 0) msg = `Add ${this.tier} boxes to unlock free box${this.freeQty > 1 ? 'es' : ''}`;
    else if (remaining > 0) msg = `Add ${remaining} more`;
    else msg = `🎉 ${this.freeQty} FREE BOX${this.freeQty > 1 ? 'ES' : ''} UNLOCKED!`;
    [this.$.msg, this.$m.msg].forEach((el) => { if (el) el.textContent = msg; });

    this.cards.forEach((c) => {
      const it = this.state.get(c.dataset.flavorId);
      c.classList.toggle('is-added', it.qty > 0);
      c.querySelector('[data-qty-value]').textContent = it.qty;
      const inc = c.querySelector('[data-increment]');
      const dec = c.querySelector('[data-decrement]');
      if (inc) inc.disabled = qty >= this.tier;
      if (dec) dec.disabled = it.qty === 0;
    });

      const freeWord = this.freeQty > 1 ? `${this.freeQty} free boxes` : 'your free box';
    const ctaLabel = qty < this.tier
      ? `Add ${remaining} more to get ${freeWord}`
      : 'Add to cart';
    const showPrices = qty >= this.tier;

    [
      { btn: this.$.btn, label: this.$.label, prices: this.$.prices, strike: this.$.strike, total: this.$.total },
      { btn: this.$m.btn, label: this.$m.label, prices: this.$m.prices, strike: this.$m.strike, total: this.$m.total }
    ].forEach((set) => {
      if (!set.btn) return;
      set.btn.disabled = qty < this.tier;
      set.label.textContent = ctaLabel;
      if (showPrices) {
        set.prices.hidden = false;
        set.strike.textContent = this.fmt(subtotal);
        set.total.textContent = this.fmt(total);
      } else {
        set.prices.hidden = true;
      }
    });
  }

    async checkout() {
    const { qty } = this.totals();
    if (qty < this.tier) return;

    const items = [];
    this.state.forEach((item) => {
      if (item.qty > 0 && item.variantId) {
        items.push({
          id: parseInt(item.variantId, 10),
          quantity: item.qty,
          properties: { _bundle: '4-box-offer' }
        });
      }
    });

    if (items.length === 0) {
      console.warn('[ChunkyFit] No variant IDs configured. Set products on each flavor block in the theme editor.');
      [this.$.btn, this.$m.btn].forEach((b) => {
        if (!b) return;
        b.disabled = true;
        const label = b.querySelector('[data-cta-label], [data-m-label]');
        if (label) label.textContent = 'Configure products in theme editor';
      });
      return;
    }

    [this.$.btn, this.$m.btn].forEach((b) => { if (b) b.disabled = true; });

    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items })
      });
      if (!res.ok) throw new Error('Add to cart failed: ' + res.status);

      // Open cart drawer so customer sees free box(es) at $0
      if (window.Upcart && typeof window.Upcart.open === 'function') {
        window.Upcart.open();
      } else if (window.Upcart && typeof window.Upcart.openCart === 'function') {
        window.Upcart.openCart();
      } else {
        ['cart:refresh', 'cart:open', 'cart:updated', 'cart-drawer:open'].forEach((name) => {
          document.dispatchEvent(new CustomEvent(name, { bubbles: true, detail: { items } }));
        });
        const trigger = document.querySelector('[data-cart-drawer-toggle], [data-action="open-cart-drawer"], .header__cart-toggle, a[href="/cart"]');
        if (trigger) trigger.click();
      }
      [this.$.btn, this.$m.btn].forEach((b) => { if (b) b.disabled = false; });
    } catch (err) {
      console.error('[ChunkyFit] Checkout error:', err);
      [this.$.btn, this.$m.btn].forEach((b) => { if (b) b.disabled = false; });
      alert('Something went wrong adding to cart. Please try again.');
    }
  }
}

if (!customElements.get('cf-bundle-builder')) {
  customElements.define('cf-bundle-builder', CfBundleBuilder);
}

/* ===== Gallery thumbnail click swaps hero image ===== */
(function setupGallerySync() {
  document.addEventListener('click', (e) => {
    const thumb = e.target.closest('[data-gallery-thumb]');
    if (!thumb) return;
    document.querySelectorAll('[data-gallery-thumb]').forEach((t) => t.classList.remove('is-active'));
    thumb.classList.add('is-active');
    const url = thumb.dataset.image;
    const heroImg = document.getElementById('cfGalleryImg');
    if (url && heroImg && heroImg.tagName === 'IMG') {
      heroImg.src = url;
      heroImg.srcset = '';
    }
  });
})();

/* ===== Nutrition modal ===== */
function openCfNutrition({ name, image, galleryImage }) {
  const modal = document.getElementById('cfNutritionModal');
  if (!modal) return;
  const title = document.getElementById('cfNutritionTitle');
  const img = modal.querySelector('[data-nutrition-img]');
  const empty = modal.querySelector('[data-nutrition-empty]');
  if (title) title.textContent = (name || 'Nutrition Facts') + ' — Nutrition Facts';
  if (image) {
    img.src = image;
    img.alt = (name || '') + ' nutrition facts';
    img.hidden = false;
    if (empty) empty.hidden = true;
  } else {
    img.hidden = true;
    img.removeAttribute('src');
    if (empty) empty.hidden = false;
  }
  modal.classList.add('is-open');
}
function closeCfNutrition() {
  const modal = document.getElementById('cfNutritionModal');
  if (modal) modal.classList.remove('is-open');
}
document.addEventListener('click', (e) => {
  if (e.target.matches('[data-modal-close]')) closeCfNutrition();
  if (e.target.id === 'cfNutritionModal') closeCfNutrition();
});
document.addEventListener('keydown', (e) => {
  if (e.key === 'Escape') closeCfNutrition();
});

/* ===== Mobile sticky: reveal when inline CTA is offscreen ===== */
(function setupStickyReveal() {
  function init() {
    const inline = document.querySelector('cf-bundle-builder .cf-btn-cta[data-checkout-button]');
    const mStick = document.getElementById('cfMobileStick');
    if (!inline || !mStick || !('IntersectionObserver' in window)) return;
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        mStick.classList.toggle('is-visible', !entry.isIntersecting);
        mStick.setAttribute('aria-hidden', String(entry.isIntersecting));
      });
    }, { threshold: 0, rootMargin: '0px 0px -40px 0px' });
    io.observe(inline);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();